import {
	Card,
	createStyles,
	Divider,
	Flex,
	Group,
	Modal,
	Paper,
	Stack,
} from '@mantine/core';
import { Icon, IconButton, Text, Title } from '@repo/foundations';
import { observer } from 'mobx-react-lite';

import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useFeatureFlags } from '../../../utils/featureFlags';
import PolicyTemplates from './PolicyTemplates';

export interface CreatePolicyModalProps {
	onClose: () => void;
}

const useStyles = createStyles((theme) => ({
	card: {
		cursor: 'pointer',
		flex: 1,
		border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		'&:hover': {
			background: theme.other.getColor('surface/primary/hover'),
		},
	},
	preview: {
		borderRadius: `0 0 ${theme.radius.sm} ${theme.radius.sm}`,
		overflow: 'hidden',
		marginBottom: theme.spacing.md,
		background: 'transparent',
	},
	templatePreviewBlank: {
		height: theme.other.space[7],
		border: `1px dashed ${theme.other.getColor('border/emphasis-secondary/default')}`,
		marginBottom: theme.spacing.sm,
	},
	templatePreviewFilled: {
		height: theme.other.space[7],
		borderRadius: theme.radius.xs,
		background: theme.other.getColor('surface/info/default'),
		border: `1px dashed ${theme.other.getColor('border/emphasis-secondary/default')}`,
		marginBottom: theme.spacing.sm,
	},
	templatePreviewText: {
		color: theme.other.getColor('fill/emphasis-secondary/active'),
	},
	templateIcon: {
		'& path': {
			stroke: theme.other.getColor('border/emphasis-secondary/default'),
		},
		'& rect': {
			fill: theme.other.getColor('border/emphasis-secondary/default'),
		},
	},
}));

export const CreatePolicyModal = observer(
	({ onClose }: CreatePolicyModalProps) => {
		const [step, setStep] = useState<'step1' | 'step2'>('step1');

		const { theme, classes } = useStyles();
		const navigate = useNavigate();
		const location = useLocation();
		const { dataScanning } = useFeatureFlags();
		const navigateToNewPolicy = useCallback(() => {
			onClose();
			navigate('/policies/new', {
				state: { prev_url_path: location.pathname },
			});
		}, [navigate, location.pathname, onClose]);
		const size = dataScanning ? 720 : 480;

		if (step === 'step2') {
			return (
				<Modal
					opened
					onClose={onClose}
					title={
						<Group spacing="sm" align="center">
							<IconButton
								variant="tertiary"
								size="sm"
								iconName="chevronLeft"
								onClick={() => {
									setStep('step1');
								}}
							/>
							<Text size="md" weight="semibold">
								Select policy template
							</Text>
						</Group>
					}
					size={size}
				>
					<PolicyTemplates />
				</Modal>
			);
		}

		return (
			<Modal
				opened
				onClose={onClose}
				title={
					<Group spacing="sm" align="center">
						<Text size="md" weight="semibold">
							Create policy
						</Text>
					</Group>
				}
				size={480}
			>
				<>
					<Stack>
						<Flex justify="center">
							<Text size="sm">Select a way to get started</Text>
						</Flex>
						<Group spacing="lg" position="apart" noWrap>
							<Card
								className={classes.card}
								padding="md"
								radius="md"
								onClick={navigateToNewPolicy}
							>
								<Paper
									p={0}
									className={classes.preview}
									h={theme.other.space[28]}
								>
									<Paper radius="xs" className={classes.templatePreviewBlank} />
									<Paper radius="xs" className={classes.templatePreviewBlank} />
									<Paper radius="xs" className={classes.templatePreviewBlank} />
								</Paper>
								<Title size="sm">Blank Policy</Title>
								<Text size="sm" color="text/secondary/default">
									Build a policy with complete flexibility
								</Text>
							</Card>

							<Card className={classes.card} onClick={() => setStep('step2')}>
								<Paper
									p={0}
									className={classes.preview}
									h={theme.other.space[28]}
								>
									<Paper
										radius="xs"
										className={classes.templatePreviewFilled}
									/>
									<Group spacing="xs">
										<Paper p="3xs" className={classes.templatePreviewFilled}>
											<Icon
												name="priorityHigh"
												className={classes.templateIcon}
											/>
										</Paper>
										<Paper
											py="3xs"
											pl="xs"
											className={classes.templatePreviewFilled}
										>
											<Group spacing={0}>
												<Text
													size="sm"
													className={classes.templatePreviewText}
													weight="bold"
												>
													HIPAA
												</Text>
												<Icon name="smallX" className={classes.templateIcon} />
											</Group>
										</Paper>
										<Paper
											py="3xs"
											pl="2xs"
											className={classes.templatePreviewFilled}
										>
											<Group spacing={0}>
												<Text
													size="sm"
													className={classes.templatePreviewText}
													weight="bold"
												>
													GDPR
												</Text>
												<Icon name="smallX" className={classes.templateIcon} />
											</Group>
										</Paper>
									</Group>
									<Paper className={classes.templatePreviewFilled} />
								</Paper>
								<Title size="sm">Template</Title>
								<Text size="sm" color="text/secondary/default">
									Start faster with a pre-built template
								</Text>
							</Card>
						</Group>
					</Stack>
					<Divider
						sx={{
							margin: `${theme.spacing.xl} -${theme.spacing.md} ${theme.spacing.lg} -${theme.spacing.md}`,
						}}
						orientation="horizontal"
					/>
					<Text size="sm">
						Learn more about{' '}
						<a
							href="https://docs.secoda.com/features/policies"
							target="_blank"
							rel="noreferrer noopener"
						>
							creating policies
						</a>
					</Text>
				</>
			</Modal>
		);
	}
);

CreatePolicyModal.displayName = 'CreatePolicyModal';
export default CreatePolicyModal;
